// CriticalOrderOverlay - Plein écran 30s strict + sirène stridente Web Audio API
// S'affiche dès qu'un évent `order_broadcast` arrive via WebSocket pour un livreur EN LIGNE.
// L'utilisateur peut :
//   - ACCEPTER (gros bouton vert) → POST /accept-broadcast → ferme overlay
//   - laisser expirer → 30s timer + auto-fermeture + broadcast next courier
//
// L'overlay s'affiche EN PLUS du flux normal de la liste broadcast.
import { useEffect, useRef, useState } from "react";
import { MapPin, Clock, Coins, X, AlertTriangle, Volume2 } from "lucide-react";
import { getAlarmCtx, isAlarmAudioReady } from "@/utils/alarmAudio";

const SIREN_DURATION_MS = 30000; // 30s strict

/**
 * Sirène stridente Web Audio (alternance 800Hz <-> 1400Hz, gain élevé).
 * Retourne une fonction stop().
 */
function startSiren() {
  let stopped = false;
  const audioCtx = getAlarmCtx();
  if (!audioCtx) return () => {};

  const masterGain = audioCtx.createGain();
  masterGain.gain.value = 0.9; // fort
  masterGain.connect(audioCtx.destination);

  const osc1 = audioCtx.createOscillator();
  osc1.type = "sawtooth";
  osc1.frequency.value = 800;
  osc1.connect(masterGain);

  const osc2 = audioCtx.createOscillator();
  osc2.type = "square";
  osc2.frequency.value = 1400;
  const gain2 = audioCtx.createGain();
  gain2.gain.value = 0;
  osc2.connect(gain2);
  gain2.connect(masterGain);

  osc1.start();
  osc2.start();

  // Alternance toggle des deux oscillateurs toutes les 350ms pour effet sirène stridente
  let toggle = false;
  const interval = setInterval(() => {
    if (stopped) return;
    toggle = !toggle;
    try {
      const now = audioCtx.currentTime;
      if (toggle) {
        masterGain.gain.setTargetAtTime(0.0, now, 0.05);
        gain2.gain.setTargetAtTime(0.9, now, 0.05);
      } else {
        masterGain.gain.setTargetAtTime(0.9, now, 0.05);
        gain2.gain.setTargetAtTime(0.0, now, 0.05);
      }
    } catch (e) {
      console.debug("[Siren] toggle failed:", e);
    }
  }, 350);
  const buzz = setInterval(() => navigator.vibrate?.([600, 200, 600]), 2000);

  return () => {
    if (stopped) return;
    stopped = true;
    clearInterval(interval);
    clearInterval(buzz);
    try {
      navigator.vibrate?.(0);
      osc1.stop();
      osc2.stop();
      masterGain.disconnect();
    } catch (e) {
      console.debug("[Siren] stop failed:", e);
    }
  };
}

export default function CriticalOrderOverlay({ order, onAccept, onExpire, onDismiss, onDecline }) {
  const [remaining, setRemaining] = useState(30);
  const sirenStopRef = useRef(null);
  const tickRef = useRef(null);
  const [soundBlocked, setSoundBlocked] = useState(false);

  useEffect(() => {
    if (!order) return undefined;

    setRemaining(30);
    // Vibration mobile (Android Chrome) — pattern strident
    try {
      if (navigator.vibrate) {
        navigator.vibrate([400, 100, 400, 100, 400, 100, 400, 100, 400, 100, 400]);
      }
    } catch (e) {
      console.debug("[Vibrate] error:", e);
    }
    // Sirène
    sirenStopRef.current = startSiren();
    setSoundBlocked(!isAlarmAudioReady());

    // Countdown 30s
    const startedAt = Date.now();
    tickRef.current = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startedAt) / 1000);
      const left = Math.max(0, 30 - elapsed);
      setRemaining(left);
      if (left <= 0) {
        clearInterval(tickRef.current);
        tickRef.current = null;
        try { sirenStopRef.current && sirenStopRef.current(); } catch (e) { console.debug(e); }
        if (onExpire) onExpire(order);
      }
    }, 1000);

    return () => {
      if (tickRef.current) clearInterval(tickRef.current);
      try { sirenStopRef.current && sirenStopRef.current(); } catch (e) { console.debug(e); }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order?.order_id]);

  if (!order) return null;

  const isTaxi = (order.order_type || "").toLowerCase() === "taxi";
  const fee = Number(
    order.taxi_fare ?? order.delivery_fee ?? order.fee ?? 0
  );
  const isPriority = order.priority === "top";
  const pickup = order.pickup_address || "—";
  const dropoff = order.delivery_address || "—";
  const distance = order.distance_km != null ? Number(order.distance_km).toFixed(1) : null;

  const handleAccept = async () => {
    try { sirenStopRef.current && sirenStopRef.current(); } catch (e) { console.debug(e); }
    if (tickRef.current) clearInterval(tickRef.current);
    if (onAccept) onAccept(order);
  };

  const handleDismiss = () => {
    try { sirenStopRef.current && sirenStopRef.current(); } catch (e) { console.debug(e); }
    if (tickRef.current) clearInterval(tickRef.current);
    if (onDismiss) onDismiss(order);
  };

  const handleDecline = () => {
    try { sirenStopRef.current && sirenStopRef.current(); } catch (e) { console.debug(e); }
    if (tickRef.current) clearInterval(tickRef.current);
    if (onDecline) onDecline(order);
  };

  const enableSound = () => {
    const c = getAlarmCtx();
    c?.resume().then(() => setSoundBlocked(c.state !== "running")).catch(() => {});
  };

  const pct = (remaining / 30) * 100;
  const urgent = remaining <= 10;

  return (
    <div
      className="fixed inset-0 z-[9999] bg-[#0F172A] flex flex-col items-center justify-between px-6 py-8 animate-in fade-in"
      data-testid="critical-order-overlay"
      onPointerDown={soundBlocked ? enableSound : undefined}
    >
      {soundBlocked && (
        <button
          onClick={enableSound}
          className="absolute top-5 left-5 flex items-center gap-2 bg-amber-500 text-black px-4 py-2 rounded-full text-[11px] font-black uppercase tracking-widest animate-pulse"
          data-testid="critical-overlay-enable-sound"
        >
          <Volume2 size={14} /> Activer la sonnerie
        </button>
      )}
      {/* CLOSE (X) — small, top-right */}
      <button
        onClick={handleDismiss}
        className="absolute top-5 right-5 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
        data-testid="critical-overlay-close"
        aria-label="Ignorer"
      >
        <X size={18} />
      </button>

      {/* TOP - Countdown ring */}
      <div className="flex flex-col items-center mt-2">
        <div
          className={`text-[10px] font-black uppercase tracking-[0.4em] mb-4 ${
            urgent ? "text-red-400 animate-pulse" : "text-amber-400"
          }`}
        >
          {urgent ? (
            <span className="flex items-center gap-2">
              <AlertTriangle size={14} /> URGENT — {remaining}s
            </span>
          ) : (
            <>Délai d'acceptation</>
          )}
        </div>

        <div className="relative w-44 h-44 flex items-center justify-center">
          <svg className="absolute inset-0 -rotate-90" viewBox="0 0 100 100">
            <circle cx="50" cy="50" r="46" stroke="rgba(255,255,255,0.08)" strokeWidth="6" fill="none" />
            <circle
              cx="50"
              cy="50"
              r="46"
              stroke={urgent ? "#EF4444" : "#D4AF37"}
              strokeWidth="6"
              fill="none"
              strokeDasharray={2 * Math.PI * 46}
              strokeDashoffset={(1 - pct / 100) * (2 * Math.PI * 46)}
              strokeLinecap="round"
              style={{ transition: "stroke-dashoffset 1s linear" }}
            />
          </svg>
          <div className="text-center">
            <p className="text-6xl font-black text-white tracking-tight">{remaining}</p>
            <p className="text-[9px] font-black uppercase tracking-[0.3em] text-slate-400 mt-1">secondes</p>
          </div>
        </div>
      </div>

      {/* MIDDLE - Order info */}
      <div className="w-full max-w-md bg-white/5 border border-white/10 rounded-[2rem] p-6 my-6">
        {isPriority && (
          <div
            className="mx-auto mb-4 inline-flex items-center justify-center gap-2 bg-gradient-to-r from-amber-500 to-amber-600 text-black px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-[0.3em] shadow-lg shadow-amber-500/40 animate-pulse"
            data-testid="critical-overlay-priority-badge"
          >
            ⭐ Top livreur · 30s d'avance
          </div>
        )}
        <p className="text-[10px] font-black uppercase tracking-[0.4em] text-[#D4AF37] text-center mb-5">
          {isTaxi ? "NOUVELLE COURSE TAXI" : "NOUVELLE COURSE"} · #{(order.order_id || "").substring(0, 8).toUpperCase()}
        </p>

        {/* Fee BIG */}
        <div className="text-center mb-6">
          <p className="text-5xl md:text-6xl font-black italic text-emerald-400 tracking-tight">
            {fee.toLocaleString("fr-FR")}<span className="text-lg ml-2 text-emerald-200">FCFA</span>
          </p>
          {distance && (
            <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400 mt-2">
              {distance} km · gain estimé
            </p>
          )}
        </div>

        {/* Route */}
        <div className="space-y-3">
          <div className="flex items-start gap-3 bg-white/[0.03] border border-blue-500/30 rounded-2xl p-3">
            <div className="w-9 h-9 rounded-xl bg-blue-500/20 flex items-center justify-center flex-shrink-0">
              <MapPin size={14} className="text-blue-400" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[9px] font-black text-blue-400 uppercase tracking-[0.3em]">Départ</p>
              <p className="text-sm font-black text-white truncate">{pickup}</p>
            </div>
          </div>
          <div className="flex items-start gap-3 bg-white/[0.03] border border-emerald-500/30 rounded-2xl p-3">
            <div className="w-9 h-9 rounded-xl bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
              <Coins size={14} className="text-emerald-400" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[9px] font-black text-emerald-400 uppercase tracking-[0.3em]">Destination</p>
              <p className="text-sm font-black text-white truncate">{dropoff}</p>
            </div>
          </div>
        </div>
      </div>

      {/* BOTTOM - Accept / Decline */}
      <div className="w-full max-w-md flex gap-3">
        <button
          onClick={handleDecline}
          className="w-1/3 bg-gradient-to-r from-red-600 to-red-700 text-white py-6 rounded-[2.5rem] font-black text-sm uppercase tracking-[0.2em] shadow-2xl shadow-red-500/40 active:scale-95 transition-all"
          data-testid="critical-overlay-decline"
        >
          REFUSER
        </button>
        <button
          onClick={handleAccept}
          className="flex-1 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white py-6 rounded-[2.5rem] font-black text-xl uppercase tracking-[0.3em] shadow-2xl shadow-emerald-500/40 active:scale-95 transition-all flex items-center justify-center gap-3"
          data-testid="critical-overlay-accept"
        >
          <Clock size={22} strokeWidth={3} /> ACCEPTER
        </button>
      </div>
      <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest mt-3">
        Réveille-toi livreur WÔMA — Le client t'attend
      </p>
    </div>
  );
}
